import type {
  DocumentHistory,
  GetDocumentByPathQuery,
} from '#src/graphql/generated';

import { useMutation } from '@apollo/client/react';
import {
  Box,
  Heading,
  HStack,
  Button,
  VStack,
  Text,
  Collapsible,
} from '@chakra-ui/react';
import MDEditor from '@uiw/react-md-editor';
import { useState } from 'react';
import { FiChevronDown, FiChevronRight } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';

import { toaster } from '#src/components/ui/toaster';
import { UPSERT_DOCUMENT } from '#src/graphql/mutations/upsert-document';
import { formatDocumentName } from '#src/utils/format-document-name';

/** Document branch only (parent passes null for DocumentNotFound). */
type DocumentFromQuery = Extract<
  GetDocumentByPathQuery['documentByPath'],
  { __typename: 'Document' }
> | null;

interface DocumentHistoryProps {
  /** Document from parent query; null when not found (DocumentNotFound). */
  document: DocumentFromQuery;
  /** Canonical path (document.path when document exists, else requested path from URL). */
  path: string;
  refetch: () => Promise<unknown>;
}

function DocumentHistoryContent({ document, path }: DocumentHistoryProps) {
  const navigate = useNavigate();

  const history = document?.documentHistory || [];

  if (!document) {
    return (
      <Box bg="white" shadow="lg" borderRadius="lg" p={12}>
        <VStack gap={4}>
          <Text fontSize="lg" color="gray.500" textAlign="center">
            Document not found
          </Text>
          <Text fontSize="sm" color="gray.400" textAlign="center">
            {`The document "${formatDocumentName(path).join(' > ')}" doesn't exist yet.`}
          </Text>
          <Button
            colorScheme="blue"
            onClick={() => navigate(`/documents/${path}`)}
          >
            Create Document
          </Button>
        </VStack>
      </Box>
    );
  }

  const formattedName = formatDocumentName(
    document.path ?? document.id ?? '',
  ).join(' > ');

  const formatDate = (date: Date | string) => {
    const d = typeof date === 'string' ? new Date(date) : date;
    return d.toLocaleString();
  };

  const getHistoryValue = (historyEntry: DocumentHistory) => {
    // The value is stored as JSON, extract the document value
    const historyData = historyEntry.value as any;
    if (historyData?.value?.value) {
      return historyData.value.value;
    }
    // Fallback if structure is different
    if (typeof historyData === 'string') {
      return historyData;
    }
    return JSON.stringify(historyData, null, 2);
  };

  return (
    <Box bg="white" shadow="lg" borderRadius="lg" p={6}>
      <VStack gap={4} align="stretch">
        <HStack justify="space-between" align="center">
          <Heading size="lg" color="gray.700">
            {formattedName} - History
          </Heading>
          <HStack gap={2}>
            <Button
              size="sm"
              variant="outline"
              onClick={() => navigate(`/documents/${document.path}`)}
            >
              View
            </Button>
            <Button
              size="sm"
              colorScheme="blue"
              onClick={() => navigate(`/documents/${document.path}`)}
            >
              Edit
            </Button>
          </HStack>
        </HStack>

        {history.length === 0 ? (
          <Box py={8} textAlign="center">
            <Text color="gray.500">
              No history available for this document.
            </Text>
          </Box>
        ) : (
          <VStack gap={2} align="stretch">
            {history.map((historyEntry) => {
              if (!historyEntry) return null;
              return (
                <HistoryRevision
                  key={historyEntry.id}
                  historyEntry={historyEntry as DocumentHistory}
                  formatDate={formatDate}
                  getHistoryValue={getHistoryValue}
                  documentPath={path}
                  documentId={document.id || ''}
                />
              );
            })}
          </VStack>
        )}
      </VStack>
    </Box>
  );
}

interface HistoryRevisionProps {
  historyEntry: DocumentHistory;
  formatDate: (date: Date | string) => string;
  getHistoryValue: (historyEntry: DocumentHistory) => string;
  documentPath: string;
  documentId: string;
}

function HistoryRevision({
  historyEntry,
  formatDate,
  getHistoryValue,
  documentPath,
  documentId,
}: HistoryRevisionProps) {
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();
  const [upsertDocument, { loading: reverting }] = useMutation(UPSERT_DOCUMENT);

  const handleCompare = (e: React.MouseEvent) => {
    e.stopPropagation();
    navigate(
      `/documents/${documentPath}/history/compare/${historyEntry.id}/current`,
    );
  };

  const handleRevert = async (e: React.MouseEvent) => {
    e.stopPropagation();

    const historyValue = getHistoryValue(historyEntry);

    try {
      const { data } = await upsertDocument({
        variables: {
          input: {
            id: documentId,
            path: documentPath,
            value: historyValue,
          },
        },
      });

      if (data?.upsertDocument?.document) {
        toaster.create({
          title: 'Success',
          description: 'Document reverted to this revision',
          type: 'success',
        });
        // Navigate to view the updated document
        navigate(`/documents/${documentPath}`);
      } else {
        throw new Error('Failed to revert document');
      }
    } catch (error) {
      toaster.create({
        title: 'Error',
        description: `Failed to revert document: ${
          error instanceof Error ? error.message : 'Unknown error'
        }`,
        type: 'error',
      });
    }
  };

  return (
    <Box
      border="1px solid"
      borderColor="gray.200"
      borderRadius="md"
      bg="white"
      overflow="hidden"
    >
      <Collapsible.Root open={isOpen} onOpenChange={(e) => setIsOpen(e.open)}>
        <HStack gap={2} align="stretch" width="100%">
          <Collapsible.Trigger asChild style={{ flex: 1 }}>
            <Button
              variant="ghost"
              width="100%"
              justifyContent="space-between"
              p={4}
              h="auto"
              _hover={{ bg: 'gray.50' }}
            >
              <HStack gap={3} flex={1} justify="space-between">
                <HStack gap={2}>
                  {isOpen ? (
                    <FiChevronDown size={16} />
                  ) : (
                    <FiChevronRight size={16} />
                  )}
                  <Text fontSize="sm" fontWeight="semibold" color="gray.700">
                    Revision
                  </Text>
                </HStack>
                <Text fontSize="xs" color="gray.500">
                  {formatDate(historyEntry.createdAt)}
                </Text>
              </HStack>
            </Button>
          </Collapsible.Trigger>
          <Box
            display="flex"
            alignItems="center"
            gap={2}
            px={2}
            borderLeft="1px solid"
            borderColor="gray.200"
          >
            <Button
              size="xs"
              variant="outline"
              colorScheme="blue"
              onClick={handleCompare}
            >
              Compare
            </Button>
            <Button
              size="xs"
              variant="outline"
              colorScheme="orange"
              onClick={handleRevert}
              loading={reverting}
              loadingText="Reverting..."
            >
              Revert
            </Button>
          </Box>
        </HStack>
        <Collapsible.Content>
          <Box
            px={4}
            pb={4}
            pt={2}
            width="100%"
            data-color-mode="light"
            borderTop="1px solid"
            borderColor="gray.200"
          >
            <Box
              width="100%"
              data-color-mode="light"
              border="1px solid"
              borderColor="gray.200"
              borderRadius="md"
              p={4}
              maxH="400px"
              overflow="auto"
              bg="gray.50"
            >
              <MDEditor.Markdown source={getHistoryValue(historyEntry)} />
            </Box>
          </Box>
        </Collapsible.Content>
      </Collapsible.Root>
    </Box>
  );
}

export function DocumentHistory({
  document,
  path,
  refetch,
}: DocumentHistoryProps) {
  return (
    <DocumentHistoryContent document={document} path={path} refetch={refetch} />
  );
}
